import React, { useState, useEffect } from 'react';
import { getCollection } from '../utils/dataService';
import { openPdfInNewWindow, doesFileExist } from '../utils/pdfHandler';

const LegalClaimsPage = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'full'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'Criminal', 'Civil', 'Constitutional'

  useEffect(() => {
    const fetchClaims = async () => {
      try {
        console.log('Fetching legal claims...');
        
        // Get the fallback claims first so we have them ready
        const fallbackClaims = getFallbackClaims();
        
        try {
          // Try to get claims from Firestore
          const data = await getCollection('legal-claims');
          console.log('Claims data from Firestore:', data);
          
          // If we got valid data from Firestore, use it
          if (data && data.length > 0) {
            console.log('Using Firestore data:', data.length, 'claims found');
            setClaims(data);
            setSelectedClaim(data[0]);
          } else {
            // Otherwise use fallback data
            console.log('No claims found in Firestore, using fallback data');
            setClaims(fallbackClaims);
            setSelectedClaim(fallbackClaims[0]);
          }
        } catch (firestoreError) {
          // If Firestore fails, log the error and use fallback data
          console.error('Firestore error:', firestoreError);
          console.log('Using fallback data due to Firestore error');
          setClaims(fallbackClaims);
          setSelectedClaim(fallbackClaims[0]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Unhandled error in fetchClaims:', err);
        setError('Failed to load legal claims: ' + err.message);
        
        // Even in case of catastrophic error, try to show fallback data
        try {
          const fallbackClaims = getFallbackClaims();
          setClaims(fallbackClaims);
          setSelectedClaim(fallbackClaims[0]);
        } catch (fallbackErr) {
          console.error('Failed to load fallback claims:', fallbackErr);
        }
        
        setLoading(false);
      }
    };

    fetchClaims();
  }, []);

  // Function to provide fallback claims if Firestore data is unavailable
  const getFallbackClaims = () => {
    console.log('Getting fallback claims');
    return [
      {
        id: 'claim-1',
        title: 'Unlawful Arrest Without Probable Cause',
        category: 'Criminal',
        description: 'On April 29, 2023, I was arrested after hospital discharge despite no evidence of intoxication. Police obtained my medical blood test results without a proper subpoena, violating medical privacy laws and due process.',
        legalCode: '§ 1983 Civil Rights',
        keyPoints: [
          'No field sobriety test was conducted at the scene',
          'Hospital confirmed police needed a subpoena for blood test results',
          'Arrest occurred after hospital discharge without prior notification',
          'Shenandoah PD confirmed no police report was filed by first responding officer',
          'Vehicle accident was due to mechanical failure (documented tire blowout)'
        ],
        quotes: [
          {
            text: "I'm looking and our city was hacked. And I'm not seeing that video. We've lost a lot of videos and everything else.",
            source: "Sergeant Brockoff, Shenandoah PD - May 3, 2024",
            exhibitId: "C1B",
            filePath: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf",
            audioPath: "/evidence/audio/20240503_EXHIBIT-C1A_Shenandoah-Police-Audio.mp3"
          },
          {
            text: "I don't show any callout for unit 342 to Highway 242 on that date.",
            source: "Records Officer Phillips, Shenandoah PD",
            exhibitId: "C1B",
            filePath: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "C1B",
            description: "Call transcript confirming missing dashcam footage",
            path: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf",
            type: "document"
          },
          {
            exhibitId: "C1A",
            description: "Audio recording of call with Shenandoah Police",
            path: "/evidence/audio/20240503_EXHIBIT-C1A_Shenandoah-Police-Audio.mp3",
            type: "audio"
          },
          {
            exhibitId: "D6",
            description: "Vehicle history with recall report",
            path: "/evidence/exhibits/20230427_EXHIBIT-D6_Vehicle_History_with_Recall_Report.pdf",
            type: "document"
          }
        ]
      },
      {
        id: 'claim-2',
        title: 'Procedural Violations & Chain of Custody Breach',
        category: 'Criminal',
        description: 'Following my accident on April 29, 2023, proper arrest procedures were violated. No Miranda rights were read, no proper search was conducted, and no formal booking process was followed at the scene.',
        legalCode: 'Brady Violation',
        keyPoints: [
          'Firearm magazine left at Memorial Hermann Hospital proves no search occurred',
          'No Miranda rights were read at time of alleged arrest',
          'Officers conducted no field sobriety test at the accident scene',
          'EMS records contain no mention of intoxication observations',
          'Vehicle was left unsecured at scene, violating evidence custody protocols'
        ],
        quotes: [
          {
            text: "Yes, we do have that item. It was turned in by a nurse after you left.",
            source: "Mary Beth, Memorial Hermann Hospital - December 20, 2024",
            exhibitId: "C3",
            filePath: "/evidence/documents/20241220_EXHIBIT-C3_Methodist_Hospital_Gun_Clip_Left_at_ER.pdf",
            audioPath: "/evidence/audio/20241220_EXHIBIT-C3_Memorial_Hermann_Hospital_Gun_Clip_Left_at_ER.mp3"
          },
          {
            text: "We typically only hold items for 90 days, though.",
            source: "Mary Beth, Memorial Hermann Hospital - December 20, 2024",
            exhibitId: "C3",
            filePath: "/evidence/documents/20241220_EXHIBIT-C3_Methodist_Hospital_Gun_Clip_Left_at_ER.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "C3",
            description: "Hospital call transcript confirming gun clip was left at ER",
            path: "/evidence/documents/20241220_EXHIBIT-C3_Methodist_Hospital_Gun_Clip_Left_at_ER.pdf",
            type: "document"
          },
          {
            exhibitId: "C3",
            description: "Audio recording of call with Memorial Hermann Hospital",
            path: "/evidence/audio/20241220_EXHIBIT-C3_Memorial_Hermann_Hospital_Gun_Clip_Left_at_ER.mp3",
            type: "audio"
          },
          {
            exhibitId: "D2",
            description: "Emergency medical services report",
            path: "/evidence/exhibits/20230429_EXHIBIT-D2_Hospital-Ambulatory-Summary.pdf",
            type: "document"
          }
        ]
      },
      {
        id: 'claim-3',
        title: 'Evidence Tampering & Brady Violation',
        category: 'Constitutional',
        description: 'Critical exculpatory evidence has been withheld, destroyed, or not preserved. Dashcam footage from the arresting officers was not preserved, and the prosecution failed to disclose the system hack that resulted in missing evidence.',
        legalCode: 'Brady v. Maryland',
        keyPoints: [
          'Shenandoah PD confirmed city-wide system hack resulted in lost dashcam footage',
          'Prosecution did not disclose this exculpatory information',
          'No police report was filed by first responding officer',
          'Hair follicle test showing no substances for past 90 days was dismissed',
          'EMS records containing observations contrary to intoxication charges were not provided'
        ],
        quotes: [
          {
            text: "We can confirm a city-wide system hack resulted in the loss of police dashcam footage for that date.",
            source: "Sergeant Brockoff, Shenandoah PD - May 3, 2024",
            exhibitId: "C1B",
            filePath: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf",
            audioPath: "/evidence/audio/20240503_EXHIBIT-C1A_Shenandoah-Police-Audio.mp3"
          },
          {
            text: "He didn't do a report.",
            source: "Sergeant Brockoff, Shenandoah PD - May 3, 2024",
            exhibitId: "C1B",
            filePath: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "C1B",
            description: "Call transcript confirming missing dashcam footage",
            path: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf",
            type: "document"
          },
          {
            exhibitId: "C1",
            description: "Email from public defender about missing evidence",
            path: "/evidence/exhibits/20240506_EXHIBIT-C1_Dennis-Email-Re-Evidence-Request.pdf",
            type: "document"
          },
          {
            exhibitId: "F3",
            description: "Hair follicle test results",
            path: "/evidence/exhibits/Test-Sobriety-Hair-Follicle.pdf",
            type: "document"
          }
        ]
      },
      {
        id: 'claim-4',
        title: 'Medical Negligence - Failure to Identify Seizure',
        category: 'Civil',
        description: 'Medical professionals failed to consider my documented history of seizures, instead supporting false intoxication allegations. My VA medical records clearly document my seizure condition.',
        legalCode: 'Medical Malpractice',
        keyPoints: [
          'VA medical records document seizure condition prior to the incident',
          'Symptoms consistent with post-seizure state were mischaracterized',
          'Medical professionals failed to review available medical history',
          'No proper neurological examination was conducted',
          'Hospital staff released private medical information without subpoena'
        ],
        quotes: [
          {
            text: "Montgomery County police officers approached nurse about blood tests. Nurse informed them they would need a subpoena for medical records.",
            source: "Timeline Entry - April 29, 2023",
            exhibitId: "D13",
            filePath: "/evidence/exhibits/Questionnaire-Timeline-Mayo.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "H2",
            description: "Complete medical history of seizure condition",
            path: "/evidence/exhibits/Records-Medical-VA-History.pdf",
            type: "document"
          },
          {
            exhibitId: "D4",
            description: "VA Medical History",
            path: "/evidence/exhibits/20240523_EXHIBIT-D4_VA-Medical-History.pdf",
            type: "document"
          }
        ]
      },
      {
        id: 'claim-5',
        title: 'Veteran Benefits Unlawfully Terminated',
        category: 'Civil',
        description: 'VA benefits were terminated based on improper classification as a "fugitive felon" due to the false DWI charges, causing severe financial hardship and housing instability.',
        legalCode: '38 U.S.C. § 5313B',
        keyPoints: [
          'Benefits terminated without proper due process',
          'Classification as "fugitive felon" was based on false charges',
          'VA failed to consider exculpatory evidence',
          'No warrant was active at time of benefits termination',
          'Resulted in foreclosure proceedings and loss of housing'
        ],
        quotes: [
          {
            text: "Benefits terminated due to false classification as fugitive felon based on false arrest claims.",
            source: "Timeline Entry - February 12, 2024",
            exhibitId: "D12",
            filePath: "/evidence/exhibits/20240212_EXHIBIT-D12_Termination-of-VA-Benefits.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "D12",
            description: "VA Termination Letter",
            path: "/evidence/exhibits/Records-VA-Termination.pdf",
            type: "document"
          },
          {
            exhibitId: "D13",
            description: "April 28 Timeline Document",
            path: "/evidence/exhibits/Questionnaire-Timeline-Mayo.pdf",
            type: "document"
          }
        ]
      },
      {
        id: 'claim-6',
        title: 'Bond Officer Misconduct & False Reporting',
        category: 'Criminal',
        description: 'Bond Officer Stephen Charles made false statements about compliance, leading to unwarranted bond revocation and incarceration events.',
        legalCode: 'Official Misconduct',
        keyPoints: [
          'Officer made documented false claims about interlock tampering',
          'Email evidence contradicts statements made in court',
          'Multiple instances of false reporting to court',
          'Created improper conditions not ordered by the court',
          'Pattern of harassment and intimidation'
        ],
        quotes: [
          {
            text: "Bond Officer Stephen Charles made false claims about interlock tampering despite evidence to contrary.",
            source: "Timeline Entry - May 15, 2023",
            exhibitId: "C2",
            filePath: "/evidence/exhibits/20230725_EXHIBIT-C2_Email-Probation-Conflict-Stephen-Charles.pdf"
          }
        ],
        evidence: [
          {
            exhibitId: "E5",
            description: "Bond Officer Email Correspondence",
            path: "/evidence/exhibits/Email-Bond-Harassment-Charles.pdf",
            type: "document"
          },
          {
            exhibitId: "C2",
            description: "Probation Conflict Email",
            path: "/evidence/exhibits/20230725_EXHIBIT-C2_Email-Probation-Conflict-Stephen-Charles.pdf",
            type: "document"
          }
        ]
      }
    ];
  };

  // Function to open/download a file
  const handleFileOpen = (filePath) => {
    if (filePath && doesFileExist(filePath)) {
      openPdfInNewWindow(filePath);
    } else {
      console.warn(`File path not valid: ${filePath}`);
      alert(`Sorry, this file is not available.`);
    }
  };
  
  // Function to handle audio file opening
  const handleAudioOpen = (audioPath) => {
    if (audioPath) {
      window.open(audioPath, '_blank');
    } else {
      console.warn('No audio path provided');
      alert('Sorry, this audio file is not available.');
    }
  };

  // Filter claims based on category
  const filteredClaims = categoryFilter === 'all' 
    ? claims 
    : claims.filter(claim => claim.category === categoryFilter);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-6"></div>
            <div className="h-64 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && claims.length === 0) {
    return (
      <div className="max-w-5xl mx-auto py-8">
        <div className="bg-red-50 rounded-xl shadow-md p-6">
          <h1 className="text-2xl font-bold mb-2 text-red-800">Error Loading Legal Claims</h1>
          <p className="text-red-600 mb-4">{error}</p>
          <p>Please try refreshing the page or contact the administrator.</p>
        </div>
      </div>
    );
  }

  // If no data loaded
  if (claims.length === 0) {
    return (
      <div className="max-w-5xl mx-auto py-8">
        <div className="bg-yellow-50 rounded-xl shadow-md p-6">
          <h1 className="text-2xl font-bold mb-2 text-yellow-800">No Legal Claims Found</h1>
          <p className="mb-4">No legal claims data has been loaded into the system.</p>
          <p>If you're an administrator, please use the Data Import tool to load case data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-2xl font-bold mb-2 text-blue-800">Legal Claims</h1>
            <p className="text-gray-600">
              Detailed breakdown of each legal claim with supporting evidence. 
              These claims form the basis for both criminal defense and potential civil action.
            </p>
          </div>
          <div className="flex mt-4 md:mt-0">
            <div className="bg-gray-100 rounded-lg p-1 mr-2">
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${categoryFilter === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setCategoryFilter('all')}
              >
                All
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${categoryFilter === 'Criminal' ? 'bg-red-600 text-white' : 'text-gray-700'}`}
                onClick={() => setCategoryFilter('Criminal')}
              >
                Criminal
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${categoryFilter === 'Civil' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setCategoryFilter('Civil')}
              >
                Civil
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${categoryFilter === 'Constitutional' ? 'bg-purple-600 text-white' : 'text-gray-700'}`}
                onClick={() => setCategoryFilter('Constitutional')}
              >
                Constitutional
              </button>
            </div>
            <div className="bg-gray-100 rounded-lg p-1">
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setViewMode('summary')}
              >
                Summary
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium ${viewMode === 'full' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setViewMode('full')}
              >
                Full View
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredClaims.map((claim) => (
          <div
            key={claim.id}
            className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ${
              selectedClaim?.id === claim.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''
            }`}
            onClick={() => setSelectedClaim(claim)}
          >
            <div className={`p-4 border-b flex justify-between items-center ${
              claim.category === 'Criminal' ? 'bg-red-50' : 
              claim.category === 'Civil' ? 'bg-blue-50' : 
              claim.category === 'Constitutional' ? 'bg-purple-50' : 'bg-gray-50'
            }`}>
              <h2 className="font-semibold text-lg">{claim.title}</h2>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                claim.category === 'Criminal' ? 'bg-red-100 text-red-800' : 
                claim.category === 'Civil' ? 'bg-blue-100 text-blue-800' : 
                claim.category === 'Constitutional' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {claim.category}
              </span>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">{claim.description}</p>
              
              {viewMode === 'full' && claim.keyPoints && claim.keyPoints.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Key Points:</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    {claim.keyPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {viewMode === 'full' && claim.quotes && claim.quotes.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Key Evidence:</h3>
                  <div className="space-y-3">
                    {claim.quotes.map((quote, index) => (
                      <blockquote key={index} className="pl-3 border-l-4 border-gray-200 text-sm">
                        <p className="italic">"{quote.text}"</p>
                        <footer className="mt-1 text-xs text-gray-600 flex items-center justify-between">
                          <span>{quote.source}</span>
                          <div className="flex space-x-2">
                            {quote.filePath && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFileOpen(quote.filePath);
                                }}
                                className="inline-flex items-center text-blue-600 hover:underline"
                                title={`Open ${quote.exhibitId || 'Document'}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {quote.exhibitId || 'Document'}
                              </button>
                            )}
                            {quote.audioPath && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAudioOpen(quote.audioPath);
                                }}
                                className="inline-flex items-center text-blue-600 hover:underline"
                                title="Listen to Audio"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 01-1.122-5.686" />
                                </svg>
                                Audio
                              </button>
                            )}
                          </div>
                        </footer>
                      </blockquote>
                    ))}
                  </div>
                </div>
              )}
              
              {claim.evidence && claim.evidence.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-900 mb-2">Supporting Evidence:</h3>
                  <div className="flex flex-wrap gap-2">
                    {claim.evidence.map((item, index) => (
                      <button 
                        key={index}
                        className={`px-2 py-1 rounded-md text-xs font-medium ${
                          item.type === 'audio' ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.type === 'audio') {
                            handleAudioOpen(item.path);
                          } else {
                            handleFileOpen(item.path);
                          }
                        }}
                        title={item.description || `Exhibit ${item.exhibitId || ''}`}
                      >
                        {item.type === 'audio' ? '🔊 ' : '📄 '}
                        {item.exhibitId || (item.type === 'audio' ? 'Audio' : 'Document')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-between">
                <button 
                  className="text-sm font-medium px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedClaim(claim);
                    setViewMode(viewMode === 'summary' ? 'full' : 'summary');
                  }}
                >
                  {viewMode === 'summary' ? 'View Full Details' : 'Show Summary'}
                </button>
                
                {claim.legalCode && (
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                    {claim.legalCode}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Case overview section */}
      {filteredClaims.length === 0 && (
        <div className="mt-8 p-6 bg-white rounded-xl shadow-md">
          <h2 className="text-xl font-bold mb-4 text-red-800">No Claims Found in Selected Category</h2>
          <p className="text-gray-700">
            No legal claims were found in the selected category. Please select a different category or view all claims.
          </p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={() => setCategoryFilter('all')}
          >
            View All Claims
          </button>
        </div>
      )}

      {/* Selected claim detail view */}
      {selectedClaim && viewMode === 'full' && (
        <div className="mt-8 bg-white rounded-xl shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
              selectedClaim.category === 'Criminal' ? 'bg-red-500' : 
              selectedClaim.category === 'Civil' ? 'bg-blue-500' : 
              selectedClaim.category === 'Constitutional' ? 'bg-purple-500' : 'bg-gray-500'
            }`}></span>
            {selectedClaim.title}
            <span className="ml-3 text-sm font-normal text-gray-500">{selectedClaim.legalCode}</span>
          </h2>

          <div className="md:flex md:space-x-6">
            <div className="md:w-2/3">
              <div className="mb-6">
                <h3 className="font-medium text-lg text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700">{selectedClaim.description}</p>
              </div>

              <div className="mb-6">
                <h3 className="font-medium text-lg text-gray-900 mb-2">Key Points</h3>
                <ul className="list-disc pl-5 space-y-2">
                  {selectedClaim.keyPoints && selectedClaim.keyPoints.map((point, index) => (
                    <li key={index} className="text-gray-700">{point}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="font-medium text-lg text-gray-900 mb-2">Key Evidence</h3>
                <div className="space-y-4">
                  {selectedClaim.quotes && selectedClaim.quotes.map((quote, index) => (
                    <blockquote key={index} className="pl-4 border-l-4 border-gray-300 py-2 bg-gray-50 rounded-r-md">
                      <p className="italic text-gray-700">"{quote.text}"</p>
                      <footer className="mt-2 text-sm text-gray-600">
                        <div className="flex justify-between items-center">
                          <span>{quote.source}</span>
                          <div className="flex space-x-2">
                            {quote.filePath && (
                              <button 
                                onClick={() => handleFileOpen(quote.filePath)}
                                className="inline-flex items-center px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                View Document
                              </button>
                            )}
                            {quote.audioPath && (
                              <button 
                                onClick={() => handleAudioOpen(quote.audioPath)}
                                className="inline-flex items-center px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 text-xs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 01-1.122-5.686" />
                                </svg>
                                Play Audio
                              </button>
                            )}
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  ))}
                </div>
              </div>
            </div>

            <div className="md:w-1/3 mt-6 md:mt-0">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="font-medium text-lg text-gray-900 mb-3">Supporting Evidence</h3>
                <div className="space-y-3">
                  {selectedClaim.evidence && selectedClaim.evidence.map((item, index) => (
                    <div key={index} className="p-3 bg-white rounded border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start">
                        <div className={`p-2 rounded-full mr-3 ${
                          item.type === 'audio' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {item.type === 'audio' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 01-1.122-5.686" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">Exhibit {item.exhibitId}</h4>
                          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                          <button 
                            onClick={() => item.type === 'audio' ? handleAudioOpen(item.path) : handleFileOpen(item.path)}
                            className={`w-full text-center py-1 px-3 rounded-md text-xs font-medium ${
                              item.type === 'audio' 
                                ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            }`}
                          >
                            {item.type === 'audio' ? 'Play Recording' : 'View Document'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-medium text-lg text-gray-900 mb-3">Related Claims</h3>
                  <div className="space-y-2">
                    {claims
                      .filter(claim => claim.id !== selectedClaim.id)
                      .filter(claim => claim.category === selectedClaim.category)
                      .slice(0, 3)
                      .map(claim => (
                        <button 
                          key={claim.id}
                          onClick={() => setSelectedClaim(claim)}
                          className="w-full text-left p-2 rounded hover:bg-gray-100 flex items-center"
                        >
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            claim.category === 'Criminal' ? 'bg-red-500' : 
                            claim.category === 'Civil' ? 'bg-blue-500' : 
                            claim.category === 'Constitutional' ? 'bg-purple-500' : 'bg-gray-500'
                          }`}></span>
                          <span className="text-sm">{claim.title}</span>
                        </button>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-between items-center pt-4 border-t border-gray-200">
            <button 
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md"
              onClick={() => setViewMode('summary')}
            >
              Back to Summary View
            </button>
            
            <div className="text-sm text-gray-500">
              Category: 
              <span className={`ml-2 px-2 py-1 rounded ${
                selectedClaim.category === 'Criminal' ? 'bg-red-100 text-red-800' : 
                selectedClaim.category === 'Civil' ? 'bg-blue-100 text-blue-800' : 
                selectedClaim.category === 'Constitutional' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {selectedClaim.category}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Case summary section - always visible */}
      <div className="mt-8 p-6 bg-white rounded-xl shadow-md">
        <h2 className="text-xl font-bold mb-4">Case Summary Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-100">
            <h3 className="font-medium text-red-800 mb-2">Criminal Claims</h3>
            <p className="text-sm text-gray-700 mb-2">
              {claims.filter(claim => claim.category === 'Criminal').length} claims identified relating to improper arrest procedures, evidence handling violations, and official misconduct.
            </p>
            <button 
              className="text-xs text-red-700 hover:underline"
              onClick={() => setCategoryFilter('Criminal')}
            >
              View Criminal Claims →
            </button>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h3 className="font-medium text-blue-800 mb-2">Civil Claims</h3>
            <p className="text-sm text-gray-700 mb-2">
              {claims.filter(claim => claim.category === 'Civil').length} claims identified relating to medical negligence, improper benefits termination, and financial damages.
            </p>
            <button 
              className="text-xs text-blue-700 hover:underline"
              onClick={() => setCategoryFilter('Civil')}
            >
              View Civil Claims →
            </button>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
            <h3 className="font-medium text-purple-800 mb-2">Constitutional Claims</h3>
            <p className="text-sm text-gray-700 mb-2">
              {claims.filter(claim => claim.category === 'Constitutional').length} claims identified relating to due process violations, Brady violations, and other constitutional rights issues.
            </p>
            <button 
              className="text-xs text-purple-700 hover:underline"
              onClick={() => setCategoryFilter('Constitutional')}
            >
              View Constitutional Claims →
            </button>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Key Case Facts</h3>
          <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
            <li>Incident occurred on April 29, 2023 following a vehicular accident on Highway 242</li>
            <li>Medical evidence supports seizure condition rather than intoxication</li>
            <li>Critical dashcam footage from arresting officers was lost due to a city-wide system hack</li>
            <li>Firearm magazine left at hospital proves no proper search or arrest procedures were followed</li>
            <li>VA benefits were improperly terminated based on false "fugitive felon" classification</li>
            <li>Hair follicle testing confirmed no substances present in system for 90 days prior to incident</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LegalClaimsPage;